import React, { forwardRef, useImperativeHandle, useState } from 'react';

import { Box, IconButton, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { green } from '@material-ui/core/colors';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Paper from '@material-ui/core/Paper';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { getListAllAPI } from '../../services';
import {
  writeCodigoBarrasCDM,
  SECONDARY_COLOR,
  formatMoeda,
  formatPeso,
  formatDateDDMMYYYYHHmm,
} from '../../utils';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: SECONDARY_COLOR,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const useStyles = makeStyles((theme) => ({
  rootDialogo: {
    borderRadius: '25px',
  },
  btnAtions: {
    borderRadius: '25px',
    width: '150px',
  },
  btnAtionsDisabled: {
    borderRadius: '25px',
    width: '150px',
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  pendente: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    color: 'white',
    backgroundColor: '#f44336',
    cursor: 'pointer',
  },
  confirmado: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    color: 'white',
    backgroundColor: '#4caf50',
    cursor: 'pointer',
  },
}));

const COR_STATUS = {
  Isento: '#fbc02d',
  Pago: '#4caf50',
  Pendente: '#ed6c02',
};

const Secao = ({ titulo, destaque, children }) => (
  <Paper
    variant="outlined"
    style={{
      padding: '16px 20px',
      marginTop: 16,
      borderColor: destaque || undefined,
      borderWidth: destaque ? 2 : 1,
      backgroundColor: destaque ? `${destaque}1f` : undefined,
    }}
  >
    <Typography
      variant="subtitle2"
      style={{ textTransform: 'uppercase', color: '#737477', marginBottom: 12 }}
    >
      {titulo}
    </Typography>
    {children}
  </Paper>
);

const Campos = ({ children }) => (
  <Box
    display="grid"
    style={{
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 16,
    }}
  >
    {children}
  </Box>
);

const Campo = ({ label, valor }) => (
  <div>
    <div style={{ fontSize: 12, color: '#737477' }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 500 }}>{valor || '-'}</div>
  </div>
);

const DEFAULT_ID = -1;

const SidebarInputs = forwardRef(({ handleClose }, ref) => {
  const [open, setOpen] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [id, setId] = useState(DEFAULT_ID);

  const [carregando, setCarregando] = useState(false);
  const [openRow, setOpenRow] = useState(null);

  const [dados, setDados] = useState({
    data: '-',
    valor: '-',
    peso: '-',
    valor_unitario: '-',
    pago: '-',
    sessao: '',
    caixa: '-',
    operador: '-',
    fornecedor: {
      nome: '-',
      razaoSocial: '-',
      nomeFantasia: '-',
    },
    nfes: [],
    parcelas: [],
    codigo: '-',
    user: '-',
    gerente: '-',
    testemunha: '-',
    isento: false,
  });

  const handleCloseDialog = () => {
    setOpen(false);
    handleClose();
  };

  function formatFornecedor(data) {
    let nome = '-';
    let razaoSocial = '-';
    let nomeFantasia = '-';

    if (data) {
      nome = data.nome ? data.nome : '-';
      razaoSocial = data.razao_social ? data.razao_social : '-';
      nomeFantasia = data.nome_fantasia ? data.nome_fantasia : '-';
    }

    return {
      nome,
      razaoSocial,
      nomeFantasia,
    };
  }

  function formatItens(data, valorUnitario) {
    const vetor = [];
    for (let i = 0; i < data.length; i++) {
      const total = data[i].peso * valorUnitario;
      vetor.push({
        id: data[i].id,
        peso: formatPeso(data[i].peso),
        chave: data[i].nfe.chave,
        data: formatDateDDMMYYYYHHmm(data[i].nfe.dhEmis),
        descarrego: formatMoeda(total),
        total: formatMoeda(data[i].nfe.vNF),
      });
    }
    return vetor;
  }

  function formatParcelas(data) {
    const vetor = [];
    for (let i = 0; i < data.length; i++) {
      const parcela = data[i];
      const tipoPagamento = data[i].tipoPagamento;

      vetor.push({
        id: parcela.id,
        meioPagamento: parcela?.tipoPagamento?.nome || '-',
        valor: formatMoeda(parcela.valor),
        dataPagamento: moment(new Date(parcela.data_pagamento)).format(
          'DD/MM/YYYY',
        ),
        valorRecebido:
          tipoPagamento?.id === 1 ? formatMoeda(parcela.valor_recebido) : '-',
        troco:
          tipoPagamento?.id === 1
            ? formatMoeda(parcela.valor_recebido - parcela.valor)
            : '-',
        conta: parcela?.receita?.conta?.nome || '-',
        tef: parcela.dadosTef || null,
        nParcelas: parcela.n_parcelas,
      });
    }
    return vetor;
  }

  async function getDataResource(id) {
    try {
      const data = await getListAllAPI(
        'descarrego_mercadorias',
        ['id', 'asc'],
        { id: [id] },
        [
          'fornecedor.cidade.estado',
          'itens.nfe',
          'parcelas.receita.conta',
          'parcelas.dadosTef',
          'parcelas.tipoPagamento',
          'sessao.user',
          'sessao.caixa',
          'user',
          'gerente',
          'testemunha',
        ],
      );
      if (data.data.length > 0) {
        setId(data.data[0].id);
        //
        setDados({
          data: formatDateDDMMYYYYHHmm(new Date(data.data[0].data)),
          valor: formatMoeda(data.data[0].valor),
          peso: formatPeso(data.data[0].peso),
          valor_unitario: `${formatMoeda(data.data[0].valor_unitario)} / t`,
          pago: data.data[0].pago ? 'Pago' : 'Pendente',
          pago_em: data.data[0].pago_em
            ? formatDateDDMMYYYYHHmm(new Date(data.data[0].pago_em))
            : '-',
          sessao: data.data[0]?.sessao?.id || '-',
          caixa: data.data[0]?.sessao?.caixa?.nome || '-',
          operador: data.data[0]?.sessao?.user?.username || '-',
          fornecedor: formatFornecedor(data.data[0].fornecedor),
          nfes: formatItens(data.data[0].itens, data.data[0].valor_unitario),
          parcelas: formatParcelas(data.data[0].parcelas),
          codigo: writeCodigoBarrasCDM(data.data[0].id),
          user: data.data[0]
            ? data.data[0].user
              ? data.data[0].user.username
              : '-'
            : '-',
          gerente: data.data[0]
            ? data.data[0].gerente
              ? data.data[0].gerente.username
              : '-'
            : '-',
          testemunha: data.data[0]
            ? data.data[0].testemunha
              ? data.data[0].testemunha.username
              : '-'
            : '-',
          isento: data.data[0].isento ? true : false,
        });
        setCarregando(false);
      } else {
        throw 'Esta compra não existe!';
      }
    } catch (erros) {
      enqueueSnackbar(`${erros}`, {
        variant: 'error',
      });
      handleCloseDialog(-1);
    }
  }

  useImperativeHandle(ref, () => ({
    handleOpen(id) {
      setOpen(true);
      setCarregando(true);
      setOpenRow(null);
      getDataResource(id);
    },
  }));

  const statusDescarrego = dados.isento ? 'Isento' : dados.pago;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      disableBackdropClick
      disableEscapeKeyDown
      fullScreen
    >
      <DialogTitle id="form-dialog-title">{`Descarrego Mercadoria nº ${id}`}</DialogTitle>
      <DialogContent dividers>
        <div style={{ height: '100%' }}>
          <Box
            display={{ xs: 'block', sm: 'flex' }}
            flexDirection="column"
            marginBottom="30px"
            height="100%"
          >
            {carregando ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '300px',
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              <Box p="1em" maxWidth="1000px" width="100%" mx="auto">
                <Paper
                  variant="outlined"
                  style={{
                    padding: '16px 20px',
                    borderLeft: `8px solid ${COR_STATUS[statusDescarrego]}`,
                  }}
                >
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ gap: 16 }}
                  >
                    <div>
                      <Chip
                        label={statusDescarrego}
                        style={{
                          backgroundColor: COR_STATUS[statusDescarrego],
                          color: dados.isento ? '#212121' : 'white',
                          fontWeight: 'bold',
                          fontSize: 16,
                          height: 36,
                        }}
                      />
                      <div style={{ marginTop: 8, color: '#737477' }}>
                        {`Criado em ${dados.data} · Código ${dados.codigo}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: '#737477' }}>
                        {dados.isento
                          ? 'Valor de referência (não cobrado)'
                          : 'Valor do descarrego'}
                      </div>
                      <div
                        style={{
                          fontSize: 32,
                          fontWeight: 'bold',
                          textDecoration: dados.isento
                            ? 'line-through'
                            : 'none',
                        }}
                      >
                        {dados.valor}
                      </div>
                      <div style={{ color: '#737477' }}>
                        {`${dados.peso} · ${dados.valor_unitario}`}
                      </div>
                    </div>
                  </Box>
                </Paper>

                {dados.isento ? (
                  <Secao titulo="Autorização da isenção" destaque="#fbc02d">
                    <Campos>
                      <Campo
                        label="Responsável pelo recebimento"
                        valor={dados.user}
                      />
                      <Campo label="Gerente" valor={dados.gerente} />
                      <Campo label="Testemunha" valor={dados.testemunha} />
                    </Campos>
                    <Box mt="12px" color="#737477">
                      Isento de pagamento: não passou pelo caixa e não gerou
                      receita.
                    </Box>
                  </Secao>
                ) : (
                  <Secao titulo="Recebimento">
                    <Campos>
                      <Campo
                        label="Responsável pelo recebimento"
                        valor={dados.user}
                      />
                    </Campos>
                  </Secao>
                )}

                {!dados.isento && (
                  <Secao titulo="Pagamento">
                    {dados.pago === 'Pago' ? (
                      <>
                        <Campos>
                          <Campo label="Pago em" valor={dados.pago_em} />
                          <Campo label="Sessão" valor={`${dados.sessao}`} />
                          <Campo label="Caixa" valor={dados.caixa} />
                          <Campo label="Operador" valor={dados.operador} />
                        </Campos>
                        <Box mt="16px" />
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small" aria-label="parcelas">
                            <TableHead>
                              <TableRow>
                                <StyledTableCell />
                                <StyledTableCell>
                                  Meio de Pagamento
                                </StyledTableCell>
                                <StyledTableCell>Valor</StyledTableCell>
                                <StyledTableCell>
                                  Valor Recebido
                                </StyledTableCell>
                                <StyledTableCell>Troco</StyledTableCell>
                                <StyledTableCell>Conta</StyledTableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {dados.parcelas.map((row) => (
                                <React.Fragment key={row.id}>
                                  <StyledTableRow>
                                    <StyledTableCell padding="checkbox">
                                      {row.tef && (
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            setOpenRow(
                                              openRow === row.id
                                                ? null
                                                : row.id,
                                            )
                                          }
                                        >
                                          {openRow === row.id ? (
                                            <KeyboardArrowUpIcon />
                                          ) : (
                                            <KeyboardArrowDownIcon />
                                          )}
                                        </IconButton>
                                      )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      {row.meioPagamento}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      {row.valor}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      {row.valorRecebido}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      {row.troco}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      {row.conta}
                                    </StyledTableCell>
                                  </StyledTableRow>
                                  {row.tef && openRow === row.id && (
                                    <StyledTableRow>
                                      <StyledTableCell colSpan={6}>
                                        <Box p={1} bgcolor="#f5f5f5">
                                          <strong>Dados TEF</strong>
                                          <Campos>
                                            <Campo
                                              label="NSU Sitef"
                                              valor={row.tef.nsu_sitef}
                                            />
                                            <Campo
                                              label="NSU Autorizador"
                                              valor={row.tef.nsu_autorizador}
                                            />
                                            <Campo
                                              label="Nº Autorização"
                                              valor={row.tef.numeroAutorizacao}
                                            />
                                            <Campo
                                              label="Bandeira"
                                              valor={row.tef.codigoBandeira}
                                            />
                                            <Campo
                                              label="CNPJ Adquirente"
                                              valor={row.tef.CNPJAdquirinte}
                                            />
                                            <Campo
                                              label="Parcelas"
                                              valor={`${row.nParcelas || 1}x`}
                                            />
                                          </Campos>
                                          {row.tef
                                            .comprovante_estabelecimento && (
                                            <Box mt={1}>
                                              <strong>Comprovante:</strong>
                                              <pre style={{ margin: 0 }}>
                                                {
                                                  row.tef
                                                    .comprovante_estabelecimento
                                                }
                                              </pre>
                                            </Box>
                                          )}
                                        </Box>
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  )}
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    ) : (
                      <Typography>
                        Aguardando pagamento no frente de caixa (F1).
                      </Typography>
                    )}
                  </Secao>
                )}

                <Secao titulo="Fornecedor">
                  <Campos>
                    <Campo label="Responsável" valor={dados.fornecedor.nome} />
                    <Campo
                      label="Razão Social"
                      valor={dados.fornecedor.razaoSocial}
                    />
                    <Campo
                      label="Nome Fantasia"
                      valor={dados.fornecedor.nomeFantasia}
                    />
                  </Campos>
                </Secao>

                <Secao titulo="NF-es">
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" aria-label="nfes">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Emissão</StyledTableCell>
                          <StyledTableCell>Chave</StyledTableCell>
                          <StyledTableCell>Peso</StyledTableCell>
                          <StyledTableCell>Descarrego</StyledTableCell>
                          <StyledTableCell>Total NF-e</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dados.nfes.map((row) => (
                          <StyledTableRow key={row.id}>
                            <StyledTableCell>{row.data}</StyledTableCell>
                            <StyledTableCell>{row.chave}</StyledTableCell>
                            <StyledTableCell>{row.peso}</StyledTableCell>
                            <StyledTableCell>{row.descarrego}</StyledTableCell>
                            <StyledTableCell>{row.total}</StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Secao>
              </Box>
            )}
          </Box>
        </div>
      </DialogContent>
      <DialogActions>
        <div className={classes.wrapper}>
          <Button
            color="primary"
            onClick={() => {
              handleCloseDialog();
            }}
            disabled={carregando}
            classes={{
              root: classes.btnAtions,
              disabled: classes.btnAtionsDisabled,
            }}
          >
            Fechar
          </Button>
          {carregando && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
});

export default SidebarInputs;
